import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FileText, Printer, Sparkle, ArrowClockwise, DownloadSimple, ShieldCheck, Upload, Warning } from "@phosphor-icons/react";
import { isDemoMode } from "../lib/demoStore";

// Simple markdown-ish → HTML: bold, italics, headings, lists, paragraphs
function renderMarkdown(text = "") {
    if (!text) return "";
    let html = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    html = html
        .replace(/^## (.+)$/gm, '<h2 class="font-serif text-2xl mt-6 mb-3">$1</h2>')
        .replace(/^# (.+)$/gm, '<h1 class="font-serif text-3xl mt-6 mb-3">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/^- (.+)$/gm, '<li class="ml-6 list-disc">$1</li>')
        .replace(/\n\n/g, '</p><p class="my-3">');
    return `<p class="my-3">${html}</p>`;
}

export default function Reports() {
    const [reports, setReports] = useState([]);
    const [selected, setSelected] = useState(null);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/reports").then(r => setReports(r.data));
    }, []);

    const load = async (t) => {
        setSelected(t);
        setBusy(true);
        setData(null);
        try {
            const { data: d } = await api.get(`/reports/${t.type}`);
            setData(d);
        } catch {
            toast.error("Failed to load report");
        } finally {
            setBusy(false);
        }
    };

    const regenerate = async () => {
        if (!selected) return;
        setBusy(true);
        setData(null);
        try {
            const { data: d } = await api.get(`/reports/${selected.type}?force=true`);
            setData(d);
            toast.success("Report regenerated");
        } catch {
            toast.error("Failed to regenerate");
        } finally {
            setBusy(false);
        }
    };

    const exportZip = async () => {
        try {
            const res = await api.get("/export/zip", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `andrews-export-${stamp}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Data export downloaded");
        } catch {
            toast.error("Export failed");
        }
    };

    // ---- Full JSON Backup (portable, restorable across redeploys) ----
    const fileInputRef = useRef(null);
    const [restoring, setRestoring] = useState(false);
    const [backingUp, setBackingUp] = useState(false);
    const [cloudStatus, setCloudStatus] = useState(null);
    const [cloudBusy, setCloudBusy] = useState(false);

    // Load cloud-backup status on mount
    useEffect(() => {
        api.get("/backup/cloud/status")
            .then(r => setCloudStatus(r.data))
            .catch(() => setCloudStatus({ exists: false }));
    }, []);

    const cloudSave = async () => {
        setCloudBusy(true);
        try {
            const { data } = await api.post("/backup/cloud/save");
            toast.success(`Saved to cloud — ${(data.bytes / 1024).toFixed(0)} KB, survives redeploys`);
            const s = await api.get("/backup/cloud/status");
            setCloudStatus(s.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cloud save failed");
        } finally {
            setCloudBusy(false);
        }
    };

    const cloudRestore = async () => {
        if (!window.confirm(
            "Restore your entire DB from the cloud snapshot?\n\n" +
            "Uses merge mode — existing rows update, new rows insert. " +
            "Nothing is wiped, but current unsaved changes may be overwritten."
        )) return;
        setCloudBusy(true);
        try {
            const { data } = await api.post("/backup/cloud/restore?mode=merge");
            const total = Object.keys(data.restored || {}).length;
            toast.success(`Restored ${total} collections from cloud — refresh to see all data`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cloud restore failed");
        } finally {
            setCloudBusy(false);
        }
    };

    const downloadBackup = async () => {
        setBackingUp(true);
        try {
            const res = await api.get("/backup/json", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `andrews-backup-${stamp}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Backup saved — store this file somewhere safe");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally {
            setBackingUp(false);
        }
    };

    const restoreBackup = async (file, mode) => {
        setRestoring(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("mode", mode);
            const { data: r } = await api.post("/backup/restore", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const total = Object.keys(r.restored || {}).length;
            toast.success(`Restored ${total} collections (${mode} mode) — refresh the app to see the data`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setRestoring(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onPickRestore = (mode) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const confirmMsg = mode === "replace"
            ? "REPLACE mode wipes every matching collection before restoring. This is destructive. Continue?"
            : "MERGE will upsert every row from the backup into your live DB. Continue?";
        if (!window.confirm(confirmMsg)) {
            e.target.value = "";
            return;
        }
        restoreBackup(file, mode);
    };

    return (
        <div data-testid="reports-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Reports</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">
                        Generated on demand.
                    </h1>
                    <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                        Claude Sonnet 4.5 generates a narrative from your live data.
                        Click a report, review, then use the Print button to export as PDF.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button className="btn-ghost text-sm flex items-center gap-2"
                            onClick={exportZip}
                            data-testid="reports-export-zip">
                        <DownloadSimple size={14} /> Export data (ZIP)
                    </button>
                    {data && (
                        <>
                            <button className="btn-ghost text-sm flex items-center gap-2"
                                    onClick={regenerate} disabled={busy}
                                    data-testid="report-regenerate">
                                <ArrowClockwise size={14} /> Regenerate
                            </button>
                            <button className="btn-primary text-sm flex items-center gap-2"
                                    onClick={() => window.print()}
                                    data-testid="report-print">
                                <Printer size={14} /> Print / PDF
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="hairline bg-white p-6 rounded mb-8 no-print" data-testid="cloud-vault">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="max-w-xl">
                        <div className="flex items-center gap-2 label-eyebrow mb-1">
                            <ShieldCheck size={14} /> Cloud Vault
                        </div>
                        <div className="font-serif text-2xl">Survive-redeploy backup</div>
                        <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
                            Snapshots your entire DB into Emergent Object Storage — the one place
                            that <strong>survives redeploys</strong>. On the next redeploy the app
                            auto-restores from this snapshot at startup, so you never re-upload
                            transactions again. Hit "Save now" after big changes.
                        </p>
                        {cloudStatus?.exists ? (
                            <div className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
                                Last snapshot: <strong>{cloudStatus.generated_at?.slice(0, 19).replace("T", " ")}</strong>
                                {" · "}{(cloudStatus.bytes / 1024).toFixed(0)} KB
                                {" · "}{Object.values(cloudStatus.counts || {}).reduce((a, b) => a + b, 0)} docs across {Object.keys(cloudStatus.counts || {}).length} collections
                            </div>
                        ) : (
                            <div className="mt-3 text-xs" style={{ color: "#b45309" }}>
                                No cloud snapshot yet — click "Save now" to create the first one.
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <button className="btn-primary text-sm flex items-center gap-2"
                                onClick={cloudSave} disabled={cloudBusy}
                                data-testid="cloud-vault-save-btn">
                            <ShieldCheck size={14} />
                            {cloudBusy ? "Saving…" : "Save now (cloud)"}
                        </button>
                        <button className="btn-ghost text-xs flex items-center gap-2 justify-center"
                                onClick={cloudRestore} disabled={cloudBusy || !cloudStatus?.exists}
                                data-testid="cloud-vault-restore-btn">
                            <Upload size={13} /> Restore from cloud
                        </button>
                    </div>
                </div>
            </div>

            <div className="hairline bg-white p-6 rounded mb-8 no-print" data-testid="backup-vault">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="max-w-xl">
                        <div className="flex items-center gap-2 label-eyebrow mb-1">
                            <ShieldCheck size={14} /> Data Vault
                        </div>
                        <div className="font-serif text-2xl">Full JSON Backup</div>
                        <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
                            Downloads every transaction, balance-sheet row, tax pack,
                            receipt record and setting as a single portable JSON file.
                            <strong> Save this locally before any redeploy</strong> — you can
                            restore it here afterwards to bring your data back.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            className="btn-primary text-sm flex items-center gap-2"
                            onClick={downloadBackup}
                            disabled={backingUp}
                            data-testid="backup-download-btn">
                            <DownloadSimple size={14} />
                            {backingUp ? "Preparing…" : "Download backup (.json)"}
                        </button>
                        <label className="btn-ghost text-sm flex items-center gap-2 cursor-pointer justify-center"
                               data-testid="backup-restore-merge">
                            <Upload size={14} />
                            {restoring ? "Restoring…" : "Restore (merge)"}
                            <input type="file" accept="application/json" hidden
                                   ref={fileInputRef}
                                   onChange={onPickRestore("merge")}
                                   disabled={restoring}
                                   data-testid="backup-restore-merge-input" />
                        </label>
                        <label className="text-xs flex items-center gap-2 cursor-pointer justify-center"
                               style={{ color: "var(--muted)" }}
                               data-testid="backup-restore-replace">
                            <Warning size={12} weight="fill" style={{ color: "#c2410c" }} />
                            <span>Restore (replace / destructive)</span>
                            <input type="file" accept="application/json" hidden
                                   onChange={onPickRestore("replace")}
                                   disabled={restoring}
                                   data-testid="backup-restore-replace-input" />
                        </label>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-8 no-print">
                {reports.map(r => (
                    <button key={r.type}
                            onClick={() => load(r)}
                            data-testid={`report-card-${r.type}`}
                            className={`hairline p-5 rounded text-left transition-colors ${
                                selected?.type === r.type
                                    ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                    : "bg-white hover:bg-black/5"
                            }`}>
                        <FileText size={18}
                                  style={selected?.type === r.type ? { color: "var(--bone)" } : { color: "var(--muted)" }} />
                        <div className="font-serif text-lg mt-3">{r.title}</div>
                        <div className="text-xs mt-2 opacity-75">{r.description}</div>
                    </button>
                ))}
            </div>

            {busy && (
                <div className="p-10 text-center text-sm" style={{ color: "var(--muted)" }}
                     data-testid="report-loading">
                    <Sparkle size={20} className="inline animate-pulse" /> Generating…
                </div>
            )}

            {data && (
                <article className="hairline bg-white p-10 rounded print:shadow-none print:border-none"
                         data-testid="report-content">
                    <header className="mb-6 pb-4 border-b" style={{ borderColor: "var(--line)" }}>
                        <div className="label-eyebrow">{isDemoMode() ? "Family Office" : "Andrews Family Office"}</div>
                        <h1 className="font-serif text-3xl mt-2">{data.meta.title}</h1>
                        <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                            Generated {new Date(data.generated_at).toLocaleString("en-AU")}
                        </div>
                    </header>
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(data.narrative) }}
                         className="report-body text-sm leading-relaxed" />
                    <details className="mt-8 text-xs" style={{ color: "var(--muted)" }}>
                        <summary className="cursor-pointer">Source data</summary>
                        <pre className="mt-2 p-3 bg-[var(--line)]/40 rounded overflow-auto max-h-96 text-[10px]">
                            {JSON.stringify(data.data, null, 2)}
                        </pre>
                    </details>
                </article>
            )}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white; }
                }
            `}</style>
        </div>
    );
}
